import type { EventDate } from '~/composables/types/event'
import { useKftApiPaginatedFetch } from '~/composables/useKftApiPaginatedFetch'
import type { KftApiParams } from '~/utils/kftApiUtils'

/**
 * Event Dates (also called Events in other places)
 * are the actual buyable thing we have on our website.
 * Different than event description, this is what goes into the cart
 * if it does not have a SOLD_OUT or other impeding statuses
 * */

interface UseEventDatesOptions {
  query?: KftApiParams
  accumulateDates?: boolean
}

export const useFetchEventDates = async (
  eventId: string | Ref<string>,
  options: UseEventDatesOptions | undefined = undefined
) => {
  const eventIdValue: string = toValue(eventId)
  const accumulatedDates = ref<EventDate[] | null>(
    options?.accumulateDates ? [] : null
  )

  const { execute, data, error, pending, pagination } =
    await useKftApiPaginatedFetch<EventDate[]>(
      `/v1/store/events/${eventIdValue}/dates`,
      {
        method: 'GET',
        query: options?.query,
        fetchOptions: {
          key: `event-dates-${eventIdValue}-${options?.query?.search}`,
        },
      }
    )

  const updateAccumulatedDates = () => {
    if (!data.value || !data.value.data || !options?.accumulateDates) return
    accumulatedDates.value!.push(...data.value.data)
  }

  const shouldFetchNextPage = () => {
    const hasFetchedAllPages = pagination.totalPages <= pagination.currentPage
    return (
      !hasFetchedAllPages &&
      eventDateGetters.areAllDatesSoldOut(data.value?.data || []) &&
      error.value === null
    )
  }

  const goToNextPageUntilOpenDates = async () => {
    // if all the fetched dates are sold out, we want to fetch the next page
    // until we find at least one open date or get to the end of the list
    for (let i = 0; shouldFetchNextPage() && i < 2; i++) {
      if (options?.accumulateDates) {
        accumulatedDates.value = accumulatedDates.value!.filter(
          (date: EventDate) => !eventDateGetters.getDateIsSoldOut(date)
        )
      }

      pagination.goToNextPage()

      // [TODO]: using execute is a workaround to avoid not rendering the next page,
      //   it cancels the request if the next page is already being fetched
      //   instead of waiting for the current request to finish
      await execute()
    }
  }

  // watch current page and accumulate dates
  watchEffect(() => {
    updateAccumulatedDates()
  })

  await goToNextPageUntilOpenDates()

  return {
    pending,
    data,
    accumulatedDates,
    error,
    pagination: readonly(pagination),
  }
}
